import { Link } from "react-router-dom";
import { ArrowRight, Check, TestTube2 } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { DrugSources } from "@/components/site/company/CompanyShared";
import { useLang } from "@/i18n/LangContext";
import { drugTestingPages } from "@/i18n/drugTestingPages";
import {
  companyDrugQuoteHref,
  drugTestingHref,
  privateDrugBookingHref,
  privateDrugEnquiryHref,
  privateDrugPackages,
  type DrugPurpose,
  type DrugSampleKey,
} from "@/data/drugTesting";

export const useDrugTestingCopy = () => {
  const { lang } = useLang();
  return { lang, copy: drugTestingPages[lang] };
};

export const DrugTestingPageShell = ({
  breadcrumb,
  children,
}: {
  breadcrumb?: { label: string; to?: string }[];
  children: React.ReactNode;
}) => {
  const { lang, copy } = useDrugTestingCopy();
  const items = [
    { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
    {
      label: copy.navLabel,
      to: breadcrumb?.length ? drugTestingHref("hub", lang) : undefined,
    },
    ...(breadcrumb ?? []),
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {children}
        <section className="pb-6">
          <DrugSources />
        </section>
        <Breadcrumbs items={items} />
      </main>
      <Footer />
    </div>
  );
};

export const DrugTestingHero = ({
  label,
  title,
  children,
}: {
  label: string;
  title: string;
  children: React.ReactNode;
}) => {
  const { copy } = useDrugTestingCopy();
  return (
    <PageHeroCard compact>
      <div className="animate-fade-up max-w-2xl">
        <span className="inline-flex h-7 items-center justify-center rounded-full bg-white/20 px-3.5 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
          {copy.privatePrefix} · {label}
        </span>
        <h1 className="mt-4 text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl">
          {title}
        </h1>
        <div className="mt-5 space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
          {children}
        </div>
      </div>
    </PageHeroCard>
  );
};

const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, value), template);

export const SampleTypeChoices = ({ purpose = "privat" }: { purpose?: DrugPurpose }) => {
  const { lang, copy } = useDrugTestingCopy();
  const s = copy.samples;

  return (
    <section id="provtyper" className="scroll-mt-24 py-14 lg:py-16">
      <div className="container-wide">
        <span className="text-sm font-semibold uppercase tracking-wider text-primary">{s.eyebrow}</span>
        <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          {s.title}
        </h2>
        <p className="mt-4 max-w-3xl text-base text-muted-foreground">{s.intro}</p>
        {purpose === "intyg" ? (
          <p className="mt-3 max-w-3xl text-base text-muted-foreground">{s.intygNote}</p>
        ) : null}

        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {(["saliv", "urin"] as const).map((type) => {
            const pack = privateDrugPackages[type];
            const detailHref =
              type === "saliv" ? drugTestingHref("salivtest", lang) : drugTestingHref("urinprov", lang);
            return (
              <article
                key={type}
                className="flex flex-col rounded-3xl border border-border/60 bg-card p-7 shadow-card"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary">
                  <TestTube2 className="h-5 w-5" aria-hidden />
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold text-foreground">
                  {pack.label[lang]}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {pack.description[lang]}
                </p>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {s.includesLabel}
                </p>
                <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
                  {pack.analytes[lang].map((analyte) => (
                    <li key={analyte} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                      <span>{analyte}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-5 font-display text-lg font-semibold text-foreground">
                  {pack.price[lang]}
                </p>
                <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button asChild variant="hero" size="sm">
                    <Link to={privateDrugBookingHref(type, lang, purpose)}>
                      {fill(s.bookCta, { label: pack.label[lang].toLocaleLowerCase(lang === "sv" ? "sv-SE" : "en") })}
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Link>
                  </Button>
                  <Link to={detailHref} className="text-sm font-medium text-primary hover:text-primary/80">
                    {fill(s.readMore, { label: pack.label[lang].toLocaleLowerCase(lang === "sv" ? "sv-SE" : "en") })}
                  </Link>
                </div>
              </article>
            );
          })}
        </div>

        <DrugTestPriceContact purpose={purpose} />
      </div>
    </section>
  );
};

export const DrugTestPriceContact = ({ purpose = "privat" }: { purpose?: DrugPurpose }) => {
  const { lang, copy } = useDrugTestingCopy();
  const s = copy.samples;
  return (
    <div className="mt-8 space-y-2 text-sm text-muted-foreground">
      <p>
        {s.extraTests}{" "}
        <Link to={privateDrugEnquiryHref(lang, purpose)} className="font-medium text-primary hover:text-primary/80">
          {s.priceListCta}
        </Link>
      </p>
      <p>
        {s.companyQuote}{" "}
        <Link to={companyDrugQuoteHref(lang)} className="font-medium text-primary hover:text-primary/80">
          {s.companyQuoteCta}
        </Link>
      </p>
    </div>
  );
};

export const SampleDetailContent = ({ type }: { type: DrugSampleKey }) => {
  const { lang, copy } = useDrugTestingCopy();
  const s = copy.samples;
  const pack = privateDrugPackages[type];

  return (
    <>
      <DrugTestingHero label={pack.label[lang]} title={type === "saliv" ? (lang === "sv" ? "Salivtest för drogtestning" : "Saliva drug test") : (lang === "sv" ? "Urinprov för drogtestning" : "Urine drug test")}>
        <p>{pack.intro[lang]}</p>
        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap sm:items-center">
          <Button asChild variant="hero" size="xl" className="w-full sm:w-auto">
            <Link to={privateDrugBookingHref(type, lang)}>
              {fill(s.bookCta, { label: pack.label[lang].toLocaleLowerCase(lang === "sv" ? "sv-SE" : "en") })}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <span className="text-sm font-medium text-foreground">{pack.price[lang]}</span>
        </div>
      </DrugTestingHero>

      <section className="py-14 lg:py-16">
        <div className="container-wide grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              {pack.label[lang]} · {pack.price[lang]}
            </h2>
            <p className="mt-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {s.includesLabel}
            </p>
            <ul className="mt-3 space-y-2 text-base text-muted-foreground">
              {pack.analytes[lang].map((analyte) => (
                <li key={analyte} className="flex items-start gap-2">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <span>{analyte}</span>
                </li>
              ))}
            </ul>
            <DrugTestPriceContact />
            <h2 className="mt-10 font-display text-2xl font-semibold tracking-tight text-foreground">
              {s.purposeTitle}
            </h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">{s.purposeText}</p>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">{s.labText}</p>
            <h2 className="mt-8 font-display text-2xl font-semibold tracking-tight text-foreground">
              {s.turnaroundTitle}
            </h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">{s.turnaroundText}</p>
            <Link
              to={privateDrugEnquiryHref(lang, "intyg", type)}
              className="mt-5 inline-flex items-center text-sm font-medium text-primary"
            >
              {s.helpIntyg}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </div>
          <aside className="rounded-3xl border border-border/60 bg-card p-7 shadow-card h-fit">
            <h2 className="font-display text-xl font-semibold text-foreground">{s.prepTitle}</h2>
            <ul className="mt-5 space-y-3">
              {pack.preparation[lang].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm text-muted-foreground">{s.prepNote}</p>
          </aside>
        </div>
      </section>

      <SampleTypeChoices />
    </>
  );
};
